// App.js
import { useEffect, useRef, useState } from "react";
import React from 'react';
import { Box, Tooltip } from "@mui/material";
import CustomTextField from "../../../main/widgets/CustomTextField";
import { Alert, CircularProgress, Divider, FormControl, FormHelperText, Stack, Typography } from "@mui/joy";
import LinearProgress from '@mui/joy/LinearProgress';
import Grid from '@mui/joy/Grid';
import Button from '@mui/joy/Button';
import { useNavigate } from 'react-router-dom'
import CustomerOptionList from "../../../main/widgets/CustomersOptionList";
import Snackbar from '@mui/joy/Snackbar';
import axios from "axios";
import ShipmentSticker from "./ShipmentSticker";
import ShipmentAppliedRulesPanel, {
  mapStagedRowsToManualLines,
} from "./ShipmentAppliedRulesPanel";
import ShipmentPiecesPanel from "../../../shipment_pieces/views/widgets/ShipmentPiecesPanel";
import ShipmentCreatePiecesEditor, {
  EMPTY_CREATE_PIECE_ROW,
  buildCreatePiecePayload,
  validateCreatePieceRows,
  isCreatePieceRowEmpty,
} from "../../../shipment_pieces/views/widgets/ShipmentCreatePiecesEditor";
import { apiURL } from "../../../main/API/constants";
import { createShipment } from "../../../main/API/shipment";
import { extractNestedShipmentPieceErrors } from "../../../main/API/shipmentPiece";
import { translateAxiosErrorMessage, translateAggregateBackendMessage } from "../../../main/utils/masterApiError";
import { locationOptionFromApi, toLocationWritePk } from "../../../main/utils/locationDisplayName";
import { sendWhatsAppShipmentNotification } from "../../../main/API/whatsapp";
import ShipmentTypeOptionList from "../../../main/widgets/ShipmentTypeOptionList";
import { DataGrid } from "@mui/x-data-grid";
import { formatTime } from "../../../main/services/format";
import { getShipmentFormPaymentColumns } from "../../../main/widgets/shipmentPaymentDataGridColumns";
import ShipmentTagsOptionList from "../../../main/widgets/ShipmentTagsOptionList";
import SendIcon from '@mui/icons-material/Send';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TimelineIcon from '@mui/icons-material/Timeline';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import PaidOptionList from "../../../main/widgets/PaidOptionList";
import PaymentStatusOptionList from "../../../main/widgets/PaymentStatusOptionList";
import LocationOptionList from "../../../main/widgets/LocationOptionList";
import StatusOptionList from "../../../main/widgets/StatusOptionList";
import TrackingEventTypeOptionList from "../../../main/widgets/TrackingEventTypeOptionList";
import { red } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import OmrSymbol from '../../../main/components/OmrSymbol';
import NativeCheckbox from '../../../main/components/NativeCheckbox';
import { useTrackingEventPrompt } from '../../../main/hooks/useTrackingEventPrompt';
import {
  applyTrackingEventControl,
  buildShipmentTrackingSnapshot,
} from '../../../main/utils/trackingEventControl';

/** First DRF field error string from response.data[field]. */
function firstFieldError(data, field) {
  if (!data || typeof data !== 'object') return '';
  const v = data[field];
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(String).filter(Boolean).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default function ShipmentForm({ formType, shipmentID }) {
  const { t, i18n } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);
  const navigate = useNavigate();
  const customerFieldRef = useRef(null);
  const locationFieldRef = useRef(null);
  const priceFieldRef = useRef(null);

  const [isLoading, setLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(formType === 'edit' && shipmentID != null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(false);

  //control values

  //Snackbar controller
  const [successUpdate, setSuccessUpdate] = useState(false);
  const [successSentMsg, setSuccessSentMsg] = useState(false);
  const [failedUpdate, setFailedUpdate] = useState(false);
  const [failedSentMsg, setFailedSentMsg] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [locationError, setLocationError] = useState("");
  const [customerError, setCustomerError] = useState("");
  const [priceError, setPriceError] = useState("");

  //Form Values
  const [id, setId] = useState("");
  const [shipperReference, setShipperReference] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [itemsDescription, setItemsDescription] = useState("");
  const [packagesQuantity, setPackagesQuantity] = useState("");
  const [weight, setWeight] = useState("");
  const [width, setWidth] = useState("");
  const [length, setLength] = useState("");
  const [height, setHeight] = useState("");
  const [charge, setCharge] = useState("");
  const [volumetricWeight, setVolumetricWeight] = useState("");
  const [chargeableWeight, setChargeableWeight] = useState("");
  const [weightManuallySet, setWeightManuallySet] = useState(false);
  // True while the lock checkbox is saving on an existing shipment.
  const [weightLockSaving, setWeightLockSaving] = useState(false);
  // Blocks a second click before React re-renders the disabled checkbox.
  const weightLockRequestRef = useRef(false);
  const [price, setPrice] = useState("");
  const [currentLocation, setCurrentLocation] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventTypeOpt, setEventTypeOpt] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [shipmentOwner, setShipmentOwner] = useState(null);
  const [paid, setPaid] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [calculatePrice, setCalculatePrice] = useState(true);
  const [pricingTariffCode, setPricingTariffCode] = useState("");
  const [productCategoryCode, setProductCategoryCode] = useState("");
  const [meterPriceOverride, setMeterPriceOverride] = useState("");
  const [extraCost, setExtraCost] = useState("");
  const [invoiced, setInvoiced] = useState(false);
  const [tagNames, setTagNames] = useState([]);
  const [transactionDate, setTransactionDate] = useState("");
  const [createPieceRows, setCreatePieceRows] = useState([{ ...EMPTY_CREATE_PIECE_ROW }]);
  const [createPieceFieldErrors, setCreatePieceFieldErrors] = useState({});
  const [createPieceFormError, setCreatePieceFormError] = useState("");
  const [addContentModeError, setAddContentModeError] = useState("");
  // Staged manual pricing lines for Add Shipment → sent as create `manual_lines`.
  const [stagedManualLines, setStagedManualLines] = useState([]);
  // shipment for edit and view form
  const [shipment, setShipment] = useState("");
  const createPiecesSectionRef = useRef(null);
  const originalTrackingSnapshotRef = useRef(null);
  const { promptIfNeeded, trackingPromptModal } = useTrackingEventPrompt();

  const isAddFlow = formType !== "edit";

  useEffect(() => {
    if (!isAddFlow) return;
    if (calculatePrice) {
      setPrice("");
      setPriceError("");
    }
  }, [calculatePrice, isAddFlow]);

  useEffect(() => {
    if (shipmentID != null) {
      getShipment(shipmentID, { showFormLoading: formType === 'edit' });
    }
    // Mount-only load for edit/view; getShipment is stable enough for this screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const packagesQtySpecified =
    packagesQuantity !== "" && packagesQuantity != null && String(packagesQuantity).trim() !== "";
  const createPiecesHasData = createPieceRows.some((row) => !isCreatePieceRowEmpty(row));
  const createPiecesNonEmptyCount = createPieceRows.filter((row) => !isCreatePieceRowEmpty(row)).length;
  const addFlowUsesPieces = isAddFlow && createPiecesHasData && !packagesQtySpecified;
  const showPriceField = !isAddFlow || !calculatePrice;

  // True when the shipment has at least one ShipmentPiece on the server.
  // While pieces exist, the shipment-level weight/volumetric/chargeable totals
  // are auto-rolled-up by the backend (unless the admin sets `weight_manually_set`).
  // See FRONTEND_PER_PIECE_WEIGHT_PRICING_GUIDE §2.1.
  const piecesCount = (shipment && typeof shipment === 'object' && Array.isArray(shipment.pieces))
    ? shipment.pieces.length
    : 0;
  const piecesTotal = (shipment && typeof shipment === 'object' && shipment.pieces_status_summary && typeof shipment.pieces_status_summary.total === 'number')
    ? shipment.pieces_status_summary.total
    : piecesCount;
  const hasPieces = !isAddFlow && piecesTotal > 0;
  const multiPiece = piecesTotal > 1;
  const effectiveHasPieces = hasPieces || addFlowUsesPieces;
  const effectiveMultiPiece = multiPiece || (addFlowUsesPieces && createPiecesNonEmptyCount > 1);
  const showWeightLockControl = hasPieces || (isAddFlow && !packagesQtySpecified);
  // Rolled-up totals are read-only when pieces exist and the admin lock is OFF.
  const weightFieldsAutoRolledUp = effectiveHasPieces && !weightManuallySet;
  // Multi-box shipments have no meaningful aggregate W/L/H at shipment level.
  const hideShipmentDimsForMultiPiece = effectiveMultiPiece;
  const shipmentLevelDimsDisabled = weightFieldsAutoRolledUp;
  const shipmentWeightVolDisabled =
    weightFieldsAutoRolledUp || (isAddFlow && showWeightLockControl && !weightManuallySet);
  const packagesQuantityDisabled = hasPieces || (isAddFlow && createPiecesHasData);
  const createPiecesDisabled = isAddFlow && packagesQtySpecified;

  function notificationStatusIcon(notificationStatus) {
    if (notificationStatus == 'sent') {
      return (
        <Tooltip title={t('shipmentForm.notifTooltipSent')}>
          <SendIcon color="success" />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={t('shipmentForm.notifTooltipFailed')}>
        <CancelScheduleSendIcon color="error" />
      </Tooltip>
    );
  }

  // Notifications expose EN/AR display names for the dedupe-key fields
  // (status, location, event_type). Show the active UI language only.
  function renderNotifLocalized(en, ar, fallback) {
    const enStr = en != null && en !== '' ? String(en).trim() : '';
    const arStr = ar != null && ar !== '' ? String(ar).trim() : '';
    const preferAr = i18n.language?.startsWith('ar');
    const localized = preferAr ? (arStr || enStr) : (enStr || arStr);
    if (localized) return localized;
    if (fallback == null || fallback === '') return '—';
    if (typeof fallback === 'object') {
      return String(fallback.name || fallback.code || fallback.id || '—');
    }
    return String(fallback);
  }




  function validateShipmentRequiredFields() {
    setLocationError("");
    setCustomerError("");
    setPriceError("");
    setCreatePieceFieldErrors({});
    setCreatePieceFormError("");
    setAddContentModeError("");
    let ok = true;
    let firstInvalid = null;
    if (!shipmentOwner?.id) {
      setCustomerError(t('shipmentForm.valSelectCustomer'));
      ok = false;
      if (!firstInvalid) firstInvalid = "customer";
    }
    if (!toLocationWritePk(currentLocation)) {
      setLocationError(t('shipmentForm.valSelectLocation'));
      ok = false;
      if (!firstInvalid) firstInvalid = "location";
    }
    if (isAddFlow && !calculatePrice && (price === "" || price === null || String(price).trim() === "")) {
      setPriceError(t('shipmentForm.valPriceManual'));
      ok = false;
      if (!firstInvalid) firstInvalid = "price";
    }
    if (isAddFlow && packagesQtySpecified && createPiecesHasData) {
      setAddContentModeError(
        t('shipmentForm.addContentModeConflict', {
          defaultValue: 'Use either packages quantity or pieces, not both.',
        })
      );
      ok = false;
      if (!firstInvalid) firstInvalid = "packageContents";
    } else if (isAddFlow && packagesQtySpecified) {
      const qty = Number(packagesQuantity);
      if (!Number.isFinite(qty) || qty < 1) {
        setAddContentModeError(
          t('shipmentForm.valPackagesQuantityMin', {
            defaultValue: 'Packages quantity must be at least 1.',
          })
        );
        ok = false;
        if (!firstInvalid) firstInvalid = "packageContents";
      }
    } else if (isAddFlow && !packagesQtySpecified && !createPiecesHasData) {
      setAddContentModeError(
        t('shipmentForm.addContentModeRequired', {
          defaultValue: 'Enter packages quantity or add at least one piece.',
        })
      );
      ok = false;
      if (!firstInvalid) firstInvalid = "packageContents";
    } else if (addFlowUsesPieces) {
      const pieceValidation = validateCreatePieceRows(createPieceRows, t);
      if (!pieceValidation.ok) {
        ok = false;
        if (pieceValidation.errors._form) {
          setCreatePieceFormError(pieceValidation.errors._form);
        }
        const rowErrors = { ...pieceValidation.errors };
        delete rowErrors._form;
        setCreatePieceFieldErrors(rowErrors);
        if (!firstInvalid) firstInvalid = "pieces";
      }
    }
    if (!isAddFlow && !hasPieces && packagesQtySpecified) {
      const qty = Number(packagesQuantity);
      if (!Number.isFinite(qty) || qty < 1) {
        setAddContentModeError(
          t('shipmentForm.valPackagesQuantityMin', {
            defaultValue: 'Packages quantity must be at least 1.',
          })
        );
        ok = false;
        if (!firstInvalid) firstInvalid = "packageContents";
      }
    }
    return { ok, firstInvalid };
  }

  function scrollToFirstValidationError(firstInvalid) {
    if (!firstInvalid) return;
    const el =
      firstInvalid === "customer"
        ? customerFieldRef.current
        : firstInvalid === "location"
          ? locationFieldRef.current
          : firstInvalid === "price"
            ? priceFieldRef.current
            : firstInvalid === "pieces"
              ? createPiecesSectionRef.current
              : firstInvalid === "packageContents"
                ? createPiecesSectionRef.current
                : null;
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") {
        try {
          el.focus({ preventScroll: true });
        } catch {
          el.focus();
        }
      }
    }, 0);
  }

  //submit form 
  const handleSubmit = async (event) => {
    event.preventDefault();
    // The lock request is in flight; a full save would race that patch.
    if (weightLockRequestRef.current) return;
    const { ok, firstInvalid } = validateShipmentRequiredFields();
    if (!ok) {
      scrollToFirstValidationError(firstInvalid);
      return;
    }

    if (id == "") {
      if (!hasPermission(PERMISSIONS.SHIPMENTS_CREATE)) return;
    } else if (!hasPermission(PERMISSIONS.SHIPMENTS_UPDATE)) {
      return;
    }

    setIsSubmitLoading(true);

    try {
      const currentTrackingSnapshot = buildShipmentTrackingSnapshot({
        status,
        location: toLocationWritePk(currentLocation),
        eventType,
        currentLocationText:
          typeof shipment === 'object' && shipment != null && shipment.current_location != null
            ? typeof shipment.current_location === 'string'
              ? shipment.current_location
              : shipment.current_location?.id ?? ''
            : '',
      });
      const trackingDecision = await promptIfNeeded({
        isCreate: id === '',
        original: originalTrackingSnapshotRef.current,
        current: currentTrackingSnapshot,
        entityType: 'shipment',
      });
      if (trackingDecision === null) {
        return;
      }

      if (id == "") {
        await addShipment(trackingDecision);
      } else {
        await updateShipment(trackingDecision);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitLoading(false);
    }
  }

  function applyShipmentApiFieldErrors(error) {
    const data = error?.response?.data;
    if (!data || typeof data !== 'object') return false;
    const locationMsg = firstFieldError(data, 'location');
    if (locationMsg) {
      setLocationError(translateAggregateBackendMessage(locationMsg, t) || locationMsg);
      scrollToFirstValidationError('location');
      return true;
    }
    return false;
  }

  function buildShipmentCreatePayload({ pieces = null } = {}) {
    const locationPk = toLocationWritePk(currentLocation);
    const payload = {
      owner: shipmentOwner.id,
      shipper_reference: shipperReference,
      tracking_number: trackingNumber,
      items_description: itemsDescription,
      location: locationPk,
      note: note,
      status: status,
      type: type,
      paid: paid,
      payment_status: paymentStatus,
      auto_calculating_price: calculatePrice,
      pricing_tariff_code: String(pricingTariffCode ?? "").trim() || null,
      product_category_code: String(productCategoryCode ?? "").trim() || null,
      meter_price_override:
        meterPriceOverride !== "" && meterPriceOverride != null
          ? String(meterPriceOverride).trim()
          : null,
      extra_cost:
        extraCost !== "" && extraCost != null ? String(extraCost).trim() : null,
      tags: tagNames,
      transaction_date:
        transactionDate && String(transactionDate).trim() !== ""
          ? String(transactionDate).trim().slice(0, 10)
          : null,
    };

    if (Array.isArray(pieces) && pieces.length > 0) {
      payload.pieces = pieces;
      payload.weight_manually_set = weightManuallySet;
      if (weightManuallySet) {
        payload.weight = weight;
        payload.width_cm = width;
        payload.length_cm = length;
        payload.height_cm = height;
        payload.volumetric_weight = volumetricWeight;
        payload.charge = charge;
      } else {
        payload.volumetric_weight = "0";
      }
    } else {
      payload.packages_quantity = packagesQuantity;
      payload.weight = weight;
      payload.width_cm = width;
      payload.length_cm = length;
      payload.height_cm = height;
      payload.volumetric_weight = volumetricWeight;
      payload.charge = charge;
      payload.price = price;
      payload.weight_manually_set = weightManuallySet;
    }

    if (!calculatePrice && price !== "" && price != null) {
      payload.price = price;
    }

    const et = eventType && String(eventType).trim();
    if (et) {
      payload.event_type = et;
    }

    if (isAddFlow) {
      const manualLines = mapStagedRowsToManualLines(stagedManualLines);
      if (manualLines.length > 0) {
        payload.manual_lines = manualLines;
      }
    }

    return payload;
  }


  function FillAllVariables(shipment) {
    setId(shipment.id);
    setShipperReference(shipment.shipper_reference);
    setTrackingNumber(shipment.tracking_number);
    setItemsDescription(shipment.items_description);
    setPackagesQuantity(shipment.packages_quantity);
    setWeight(shipment.weight);
    setWidth(shipment.width_cm);
    setLength(shipment.length_cm);
    setHeight(shipment.height_cm);
    setCharge(shipment.charge);
    setVolumetricWeight(shipment.volumetric_weight)
    setChargeableWeight(shipment.chargeable_weight != null ? shipment.chargeable_weight : "");
    setWeightManuallySet(Boolean(shipment.weight_manually_set));
    setPrice(shipment.price);
    const locationOpt = locationOptionFromApi(shipment);
    setCurrentLocation(locationOpt);
    setNote(shipment.note);
    setStatus(shipment.status);
    const loadedEventType = shipment.event_type || '';
    setEventType(loadedEventType);
    setEventTypeOpt(loadedEventType ? { code: loadedEventType, name: loadedEventType } : null);
    setShipmentOwner(shipment.owner);
    setShipment(shipment);
    setPaid(shipment.paid);
    setPaymentStatus(shipment.payment_status || "unpaid");
    setCalculatePrice(shipment.auto_calculating_price);
    setType(shipment.type);
    setPricingTariffCode(
      shipment.pricing_tariff_code != null ? String(shipment.pricing_tariff_code) : ""
    );
    setProductCategoryCode(
      shipment.product_category_code != null ? String(shipment.product_category_code) : ""
    );
    setMeterPriceOverride(
      shipment.meter_price_override != null ? String(shipment.meter_price_override) : ""
    );
    setExtraCost(shipment.extra_cost != null ? String(shipment.extra_cost) : "");
    setNotifications(shipment.notifications);
    setInvoiced(shipment.invoiced);
    setTagNames(Array.isArray(shipment.tags) ? shipment.tags.map((t) => t.name) : []);
    if (shipment.transaction_date != null && shipment.transaction_date !== "") {
      const s = String(shipment.transaction_date);
      setTransactionDate(s.length >= 10 ? s.slice(0, 10) : s);
    } else {
      setTransactionDate("");
    }
    originalTrackingSnapshotRef.current = buildShipmentTrackingSnapshot({
      status: shipment.status,
      location: locationOpt?.id ?? null,
      eventType: shipment.event_type,
      currentLocationText:
        shipment.current_location != null
          ? typeof shipment.current_location === 'string'
            ? shipment.current_location
            : shipment.current_location?.id ?? ''
          : '',
    });
  }


  const addShipment = async (trackingDecision) => {
    setCreatePieceFieldErrors({});
    setCreatePieceFormError("");

    let piecesPayload = null;
    if (addFlowUsesPieces) {
      const pieceValidation = validateCreatePieceRows(createPieceRows, t);
      if (!pieceValidation.ok) {
        if (pieceValidation.errors._form) {
          setCreatePieceFormError(pieceValidation.errors._form);
        }
        const rowErrors = { ...pieceValidation.errors };
        delete rowErrors._form;
        setCreatePieceFieldErrors(rowErrors);
        scrollToFirstValidationError("pieces");
        return;
      }
      piecesPayload = pieceValidation.nonEmptyRows.map(buildCreatePiecePayload);
    } else if (isAddFlow && !packagesQtySpecified) {
      setAddContentModeError(
        t('shipmentForm.addContentModeRequired', {
          defaultValue: 'Enter packages quantity or add at least one piece.',
        })
      );
      scrollToFirstValidationError("packageContents");
      return;
    } else if (isAddFlow && packagesQtySpecified) {
      const qty = Number(packagesQuantity);
      if (!Number.isFinite(qty) || qty < 1) {
        setAddContentModeError(
          t('shipmentForm.valPackagesQuantityMin', {
            defaultValue: 'Packages quantity must be at least 1.',
          })
        );
        scrollToFirstValidationError("packageContents");
        return;
      }
    }

    let payload = buildShipmentCreatePayload({ pieces: piecesPayload });
    payload = applyTrackingEventControl(payload, trackingDecision);

    try {
      const response = isAddFlow
        ? await createShipment(payload)
        : await (async () => {
            axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
            return axios.post(`${apiURL}/gofar/shipment/`, payload, {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true,
            });
          })();

      if (response.status === 201 && response.data?.id) {
        window.location.href = `/shipment/${response.data.id}/EditShipment`;
        return;
      }

      setFailedUpdate(true);
      setErrorMessage(response.statusText || String(response.status));
    } catch (error) {
      const nestedPieceErrors = extractNestedShipmentPieceErrors(error);
      if (Object.keys(nestedPieceErrors).length) {
        if (nestedPieceErrors._form) {
          setCreatePieceFormError(nestedPieceErrors._form);
          delete nestedPieceErrors._form;
        }
        setCreatePieceFieldErrors(nestedPieceErrors);
        scrollToFirstValidationError("pieces");
      }
      applyShipmentApiFieldErrors(error);
      setFailedUpdate(true);
      setErrorMessage(
        translateAxiosErrorMessage(error, t)
          || error?.message
          || t('shipmentForm.snackbarCreateFailed')
      );
      console.error('There was an error creating shipment!', error);
    }
  }

  //update shipment in the database with the owner number
  const updateShipment = async (trackingDecision) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
    try {
      let payload = {
        id: id,
        owner: shipmentOwner.id,
        shipper_reference: shipperReference,
        tracking_number: trackingNumber,
        items_description: itemsDescription,
        packages_quantity: packagesQuantity,
        weight: weight,
        width_cm: width,
        length_cm: length,
        height_cm: height,
        volumetric_weight: volumetricWeight,
        charge: charge,
        price: price,
        auto_calculating_price: calculatePrice,
        location: toLocationWritePk(currentLocation),
        note: note,
        status: status,
        type: type,
        paid: paid,
        payment_status: paymentStatus,
        pricing_tariff_code: String(pricingTariffCode ?? "").trim() || null,
        product_category_code: String(productCategoryCode ?? "").trim() || null,
        meter_price_override:
          meterPriceOverride !== "" && meterPriceOverride != null
            ? String(meterPriceOverride).trim()
            : null,
        extra_cost:
          extraCost !== "" && extraCost != null ? String(extraCost).trim() : null,
        tags: tagNames,
        transaction_date: transactionDate && String(transactionDate).trim() !== "" ? String(transactionDate).trim().slice(0, 10) : null,
        weight_manually_set: weightManuallySet,
      };
      const et = eventType && String(eventType).trim();
      if (et) {
        payload.event_type = et;
      } else {
        payload.event_type = null;
      }
      payload = applyTrackingEventControl(payload, trackingDecision);
      const response = await axios.put(`${apiURL}/gofar/shipment/` + id + "/", payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        });

      if (response.status != 200) {
        const error = response.data?.message || response.status;
        setFailedUpdate(true);
        setErrorMessage(error);
        return;
      }

      setShipment(response.data);
      FillAllVariables(response.data);
      setSuccessUpdate(true);
    } catch (error) {
      applyShipmentApiFieldErrors(error);
      setFailedUpdate(true);
      setErrorMessage(
        translateAxiosErrorMessage(error, t)
          || error?.message
          || t('shipmentForm.snackbarUpdateFailed', { detail: '' })
      );
    }
  }

  // Persist the weight lock immediately. Create flow has no shipment row yet.
  const applyWeightLock = async (nextChecked) => {
    // Add Shipment stores the flag on the create payload when the form is submitted.
    if (isAddFlow) {
      setWeightManuallySet(Boolean(nextChecked));
      return;
    }
    // Ignore repeat clicks while the previous patch is still running.
    if (weightLockRequestRef.current || shipmentID == null) return;
    // Mark the request in flight before the loading state re-renders.
    weightLockRequestRef.current = true;
    setWeightLockSaving(true);
    try {
      // Same auth header the rest of this form uses for shipment writes.
      axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
      const response = await axios.patch(
        `${apiURL}/gofar/shipment/${shipmentID}/`,
        { weight_manually_set: Boolean(nextChecked) },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );
      const data = response.data || {};
      // Server is the source of truth after the patch (unlock also rolls totals back up).
      setWeightManuallySet(Boolean(data.weight_manually_set));
      setWeight(data.weight != null ? data.weight : '');
      setVolumetricWeight(data.volumetric_weight != null ? data.volumetric_weight : '');
      setChargeableWeight(data.chargeable_weight != null ? data.chargeable_weight : '');
      setShipment((prev) => (
        prev && typeof prev === 'object'
          ? { ...prev, ...data, pieces: data.pieces ?? prev.pieces }
          : data
      ));
    } catch (error) {
      setFailedUpdate(true);
      setErrorMessage(
        translateAxiosErrorMessage(error, t)
          || error?.message
          || t('shipmentForm.snackbarUpdateFailed', { detail: '' })
      );
    } finally {
      weightLockRequestRef.current = false;
      setWeightLockSaving(false);
    }
  };

  const getShipment = async (shipmentID, { showFormLoading = false } = {}) => {
    if (!hasPermission(PERMISSIONS.SHIPMENTS_VIEW)) {
      if (showFormLoading) setIsFormLoading(false);
      return;
    }

    if (showFormLoading) {
      setIsFormLoading(true);
    } else {
      setLoading(true);
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
      const response = await axios.get(`${apiURL}/gofar/shipment/` + shipmentID + "/",
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        });
      FillAllVariables(response.data);
      setShipment(response.data);
    } catch (err) {
      console.log(err.message);
    } finally {
      if (showFormLoading) {
        setIsFormLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const sendWhatsAppMessage = async (shipmentID) => {
    if (!hasPermission(PERMISSIONS.WHATSAPP_SEND_SHIPMENT_NOTIFICATION)) return;
    try {

      setLoadingMsg(true);
      const messageSID = await sendWhatsAppShipmentNotification(shipmentID).then(async response => {
        //message send successfully
        if (response.status == 200) {
          setSuccessSentMsg(true);
        }
        //failed
        else {
          setSuccessSentMsg(false);
        }

      });



    }
    catch (e) {
      setLoadingMsg(false);
    }
    setLoadingMsg(false);


  }


  if (isFormLoading) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
        <Typography level="h1" sx={{ mb: 3 }}>
          {t('shipmentForm.titleEdit')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
          <LinearProgress variant="solid" sx={{ width: '100%', maxWidth: 480 }} />
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            {t('actions.loading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
      <Box>
        <form onSubmit={handleSubmit}>
          <FormControl sx={{ width: '100%' }}>
            <Box sx={{ mb: 3 }}>
              <Typography component="div">
                <Typography level="h1">
                  {formType === 'edit' ? t('shipmentForm.titleEdit') : t('shipmentForm.titleAdd')}
                </Typography>
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {/* Customer */}
              <Grid xs={12}>
                <Box
                  ref={customerFieldRef}
                  tabIndex={-1}
                  sx={{ scrollMarginTop: 24, outline: "none" }}
                >
                  <Typography level="title-lg" sx={{ mb: 0.5 }}>{t('shipmentForm.sectionCustomer')}</Typography>
                  <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                    {t('shipmentForm.customerHint')}
                  </Typography>
                  <CustomerOptionList
                    defaultValue={shipmentOwner}
                    required={true}
                    errorMessage={customerError}
                    onChange={(event, value) => {
                      setShipmentOwner(value);
                      setCustomerError("");
                    }}
                  />
                </Box>
              </Grid>

              {/* Identification & references */}
              <Grid xs={12} sx={{ mt: 1 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography level="title-lg" sx={{ mb: 0.5 }}>{t('shipmentForm.sectionIdentification')}</Typography>
                <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  {t('shipmentForm.identificationHint')}
                </Typography>
              </Grid>

              <Grid xs={12} md={6} display={formType === "edit" ? "block" : "none"}>
                <CustomTextField
                  disabled={true}
                  type="text"
                  onChange={(event) => setId(event.target.value)}
                  value={id}
                  label={t('shipmentForm.shipmentId')}
                  fullWidth
                />
              </Grid>

              <Grid xs={12} md={6}>
                {/* Third-party tracking number is optional on create and edit. */}
                <CustomTextField
                  type="text"
                  onChange={(event) => setTrackingNumber(event.target.value)}
                  value={trackingNumber}
                  label={t('shipments.trackingNumber')}
                  fullWidth
                />
              </Grid>

              <Grid xs={12} md={6}>
                <CustomTextField
                  type="text"
                  onChange={(event) => setShipperReference(event.target.value)}
                  value={shipperReference}
                  label={t('shipments.shipperReference')}
                  fullWidth
                />
              </Grid>

              {formType === 'edit' && shipment && (
                <Grid xs={12} md={6}>
                  <CustomTextField
                    disabled={true}
                    type="text"
                    value={shipment.shipment_unit_serial_number || ''}
                    label={t('shipmentForm.shipmentUnitContainer')}
                    fullWidth
                  />
                  {!shipment.shipment_unit_serial_number && (
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      {t('shipmentForm.notAssignedContainer')}
                    </Typography>
                  )}
                  <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {t('shipmentForm.editContainerFromListHint')}
                  </Typography>
                </Grid>
              )}

              <Grid xs={12} md={6}>
                <CustomTextField
                  type="date"
                  onChange={(event) => setTransactionDate(event.target.value)}
                  value={transactionDate}
                  label={t('shipments.transactionDate')}
                  fullWidth
                />
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {t('shipmentForm.transactionDateHint')}
                </Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <ShipmentTypeOptionList
                  defaultValue={type}
                  required={true}
                  onChange={(event, value) => setType(value?.id ?? value)}
                />
              </Grid>

              {/* Package contents */}
              <Grid xs={12} sx={{ mt: 1 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography level="title-lg" sx={{ mb: 0.5 }}>{t('shipmentForm.sectionPackageContents')}</Typography>
                <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  {t('shipmentForm.packageContentsHint')}
                </Typography>
              </Grid>

              <Grid xs={12} md={8}>
                <CustomTextField
                  type="text"
                  onChange={(event) => setItemsDescription(event.target.value)}
                  value={itemsDescription}
                  label={t('shipmentForm.itemsDescription')}
                  fullWidth
                />
              </Grid>

              <Grid xs={12} md={4}>
                <CustomTextField
                  slotProps={{
                    input: {
                      min: 1,
                      max: 1000,
                      readOnly: hasPieces,
                    },
                  }}
                  required={!isAddFlow && !hasPieces}
                  type="number"
                  onChange={(event) => {
                    setPackagesQuantity(event.target.value);
                    setAddContentModeError("");
                  }}
                  value={packagesQuantity}
                  label={t('shipmentForm.packagesQuantity')}
                  endDecorator={t('shipmentForm.unitPiece')}
                  disabled={packagesQuantityDisabled}
                  fullWidth
                />
                {hasPieces && (
                  <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {t('shipmentForm.packagesQuantityRolledUpHint', {
                      defaultValue: 'Auto-rolled up from pieces. Add / remove pieces below to change.',
                    })}
                  </Typography>
                )}
                {isAddFlow && createPiecesHasData && !hasPieces && (
                  <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {t('shipmentForm.packagesQuantityPiecesLockedHint', {
                      defaultValue: 'Disabled while pieces are filled in. Clear all piece rows to use packages quantity.',
                    })}
                  </Typography>
                )}
                {!isAddFlow && addContentModeError ? (
                  <Alert variant="soft" color="danger" sx={{ mt: 0.5 }}>
                    {addContentModeError}
                  </Alert>
                ) : null}
              </Grid>

              {isAddFlow ? (
                <Grid xs={12}>
                  {addContentModeError ? (
                    <Alert variant="soft" color="danger" sx={{ mb: 1.5 }}>
                      {addContentModeError}
                    </Alert>
                  ) : null}
                  <Box
                    ref={createPiecesSectionRef}
                    tabIndex={-1}
                    sx={{ scrollMarginTop: 24, outline: 'none', width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}
                  >
                    <ShipmentCreatePiecesEditor
                      rows={createPieceRows}
                      onChange={(nextRows) => {
                        setCreatePieceRows(nextRows);
                        setCreatePieceFieldErrors({});
                        setCreatePieceFormError("");
                        setAddContentModeError("");
                      }}
                      fieldErrors={createPieceFieldErrors}
                      formError={createPieceFormError}
                      disabled={createPiecesDisabled}
                      disabledHint={
                        packagesQtySpecified
                          ? t('shipmentForm.createPiecesPackagesLockedHint', {
                              defaultValue: 'Pieces are disabled while packages quantity is set. Clear packages quantity to add pieces.',
                            })
                          : ''
                      }
                    />
                  </Box>
                </Grid>
              ) : null}

              {/* Dimensions & weight */}
              <Grid xs={12} sx={{ mt: 1 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography level="title-lg" sx={{ mb: 0.5 }}>{t('shipmentForm.sectionDimensions')}</Typography>
                <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  {t('shipmentForm.dimensionsHint')}
                </Typography>
                {(effectiveHasPieces || (showWeightLockControl && weightManuallySet)) && (
                  <Alert
                    variant="soft"
                    color={weightManuallySet ? 'warning' : 'neutral'}
                    sx={{ mb: 1.5 }}
                  >
                    <Typography level="body-sm">
                      {weightManuallySet
                        ? t('shipmentForm.rollupLockedHint', { defaultValue: 'Parent weights are locked (admin override). Piece edits will only refresh package count.' })
                        : t('shipmentForm.rollupAutoHint', { defaultValue: 'Weights and chargeable totals are rolled up from this shipment\'s pieces. Edit a piece below to update them.' })}
                    </Typography>
                  </Alert>
                )}
              </Grid>

              <Grid xs={12} sm={6} md={4}>
                <CustomTextField
                  slotProps={{
                    input: {
                      min: 0,
                      max: 999999999999999,
                      step: 0.001,
                      readOnly: shipmentWeightVolDisabled,
                    },
                  }}
                  type="number"
                  onChange={(event) => setWeight(event.target.value)}
                  value={weight}
                  label={t('shipmentForm.weight')}
                  endDecorator="kg"
                  disabled={shipmentWeightVolDisabled}
                  fullWidth
                />
              </Grid>

              {!hideShipmentDimsForMultiPiece && (
                <>
                  <Grid xs={12} sm={6} md={4}>
                    <CustomTextField
                      slotProps={{
                        input: {
                          min: 0,
                          max: 999999999999999,
                          step: 0.001,
                      readOnly: shipmentLevelDimsDisabled,
                    },
                  }}
                  type="number"
                  onChange={(event) => setLength(event.target.value)}
                  value={length}
                  label={t('shipmentForm.length')}
                  endDecorator="cm"
                  disabled={shipmentLevelDimsDisabled}
                      fullWidth
                    />
                  </Grid>

                  <Grid xs={12} sm={6} md={4}>
                    <CustomTextField
                      slotProps={{
                        input: {
                          min: 0,
                          max: 999999999999999,
                          step: 0.001,
                      readOnly: shipmentLevelDimsDisabled,
                    },
                  }}
                  type="number"
                  onChange={(event) => setWidth(event.target.value)}
                  value={width}
                  label={t('shipmentForm.width')}
                  endDecorator="cm"
                  disabled={shipmentLevelDimsDisabled}
                      fullWidth
                    />
                  </Grid>

                  <Grid xs={12} sm={6} md={4}>
                    <CustomTextField
                      slotProps={{
                        input: {
                          min: 0,
                          max: 999999999999999,
                          step: 0.001,
                      readOnly: shipmentLevelDimsDisabled,
                    },
                  }}
                  type="number"
                  onChange={(event) => setHeight(event.target.value)}
                  value={height}
                  label={t('shipmentForm.height')}
                  endDecorator="cm"
                  disabled={shipmentLevelDimsDisabled}
                      fullWidth
                    />
                  </Grid>
                </>
              )}

              <Grid xs={12} sm={6} md={4}>
                <CustomTextField
                  slotProps={{
                    input: {
                      min: 0,
                      max: 999999999999999,
                      step: 0.001,
                      readOnly: shipmentWeightVolDisabled,
                    },
                  }}
                  required={(!isAddFlow && !hasPieces) || (isAddFlow && packagesQtySpecified)}
                  type="number"
                  onChange={(event) => setVolumetricWeight(event.target.value)}
                  value={volumetricWeight}
                  label={t('shipmentForm.volumetricWeight')}
                  endDecorator="kg"
                  disabled={shipmentWeightVolDisabled}
                  fullWidth
                />
              </Grid>

              <Grid xs={12} sm={6} md={4}>
                <CustomTextField
                  slotProps={{
                    input: { readOnly: true },
                  }}
                  type="text"
                  value={chargeableWeight !== "" && chargeableWeight != null ? `${chargeableWeight}` : ''}
                  label={t('shipmentForm.chargeableWeight', { defaultValue: 'Chargeable weight' })}
                  endDecorator="kg"
                  disabled
                  fullWidth
                />
              </Grid>

              <Grid xs={12} sm={6} md={4}>
                <CustomTextField
                  slotProps={{
                    input: {
                      min: 0,
                      max: 999999999999999,
                      step: 0.001,
                    },
                  }}
                  type="number"
                  onChange={(event) => setCharge(event.target.value)}
                  value={charge}
                  label={t('shipmentForm.charge')}
                  hintTooltip={t('shipmentForm.chargeHint')}
                  endDecorator={<OmrSymbol height={16} />}
                  fullWidth
                />
              </Grid>

              {showWeightLockControl && (
                <Grid xs={12}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                      {weightLockSaving ? (
                        <CircularProgress
                          size="sm"
                          sx={{ '--CircularProgress-size': '18px', mt: '0.15rem', flexShrink: 0 }}
                        />
                      ) : null}
                      <NativeCheckbox
                        checked={weightManuallySet}
                        onChange={(event) => applyWeightLock(event.target.checked)}
                        disabled={weightLockSaving}
                        label={t('shipmentForm.weightManuallySet', { defaultValue: 'Lock shipment weights (admin override)' })}
                      />
                    </Stack>
                    <Typography level="body-xs" sx={{ color: 'neutral.600', pl: 0.25 }}>
                      {t('shipmentForm.weightManuallySetHint', {
                        defaultValue: 'When ON, the rollup will not overwrite weight / volumetric / chargeable. Use after customs paperwork is filed. Packages quantity still updates automatically.',
                      })}
                    </Typography>
                  </Stack>
                </Grid>
              )}

              {formType === 'edit' && shipmentID ? (
                <PermissionGate
                  permissions={[
                    PERMISSIONS.SHIPMENT_PIECES_LIST,
                    PERMISSIONS.SHIPMENT_PIECES_VIEW,
                    PERMISSIONS.SHIPMENT_PIECES_CUSTOMER_VIEW,
                    PERMISSIONS.SHIPMENT_PIECES_CUSTOMER_LIST_FOR_SHIPMENT,
                  ]}
                >
                  <Grid xs={12}>
                    <ShipmentPiecesPanel
                      shipmentId={id}
                      pieces={shipment && typeof shipment === 'object' ? (shipment.pieces || []) : []}
                      piecesStatusSummary={shipment && typeof shipment === 'object' ? (shipment.pieces_status_summary || null) : null}
                      onChanged={() => {
                        if (shipmentID) getShipment(shipmentID);
                      }}
                    />
                  </Grid>
                </PermissionGate>
              ) : null}

              {/* Pricing & payment */}
              <Grid xs={12} sx={{ mt: 1 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography level="title-lg" sx={{ mb: 0.5 }}>{t('shipmentForm.sectionPricing')}</Typography>
                <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  {isAddFlow ? t('shipmentForm.pricingHintAdd') : t('shipmentForm.pricingHint')}
                </Typography>
              </Grid>

              <Grid xs={12} sm={6} md={4}>
                <PaymentStatusOptionList
                  label={t('widgets.paymentStatus')}
                  required={true}
                  paymentStatus={paymentStatus}
                  setPaymentStatus={(newValue) => setPaymentStatus(newValue)}
                />
              </Grid>

              {isAddFlow ? (
                <>
                  <Grid xs={12}>
                    <Stack spacing={1}>
                      <NativeCheckbox
                        checked={calculatePrice}
                        onChange={(event) => setCalculatePrice(event.target.checked)}
                        label={t('shipmentForm.calculatePrice')}
                      />
                      <Typography level="body-xs" sx={{ color: 'neutral.600', pl: 0.25 }}>
                        {calculatePrice
                          ? t('shipmentForm.calculatePriceHelperAuto')
                          : t('shipmentForm.calculatePriceHelperManual')}
                      </Typography>
                    </Stack>
                  </Grid>
                  {calculatePrice && (
                    <Grid xs={12}>
                      <Alert variant="soft" color="neutral" sx={{ borderRadius: 'md' }}>
                        <Typography level="body-sm">{t('shipmentForm.autoPriceEngineHint')}</Typography>
                      </Alert>
                    </Grid>
                  )}
                  {!calculatePrice && (
                    <Grid xs={12} sm={8} md={6}>
                      <Box
                        ref={priceFieldRef}
                        tabIndex={-1}
                        sx={{ scrollMarginTop: 24, outline: 'none' }}
                      >
                        <CustomTextField
                          slotProps={{
                            input: {
                              min: 0,
                              max: 999999999999999,
                              step: 0.001,
                            },
                          }}
                          type="number"
                          onChange={(event) => {
                            setPrice(event.target.value);
                            setPriceError('');
                          }}
                          value={price}
                          label={t('shipmentForm.price')}
                          hintTooltip={t('shipmentForm.priceHint')}
                          endDecorator={<OmrSymbol height={16} />}
                          required
                          fullWidth
                        />
                      </Box>
                      {priceError ? (
                        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{priceError}</FormHelperText>
                      ) : null}
                    </Grid>
                  )}
                </>
              ) : (
                <>
                  <Grid xs={12} sm={8} md={6}>
                    <CustomTextField
                      slotProps={{
                        input: {
                          min: 0,
                          max: 999999999999999,
                          step: 0.001,
                        },
                      }}
                      type="number"
                      onChange={(event) => setPrice(event.target.value)}
                      value={price}
                      label={t('shipmentForm.price')}
                      hintTooltip={t('shipmentForm.priceHint')}
                      endDecorator={<OmrSymbol height={16} />}
                      disabled={calculatePrice}
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} sm={4} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <NativeCheckbox
                      checked={calculatePrice}
                      onChange={(event) => setCalculatePrice(event.target.checked)}
                      label={t('shipmentForm.calculatePrice')}
                    />
                  </Grid>
                </>
              )}

              <Grid xs={12} sm={6} md={4}>
                <CustomTextField
                  type="text"
                  label={t('shipmentForm.pricingTariffCode')}
                  value={pricingTariffCode}
                  onChange={(e) => setPricingTariffCode(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid xs={12} sm={6} md={4}>
                <CustomTextField
                  type="text"
                  label={t('shipmentForm.productCategoryCode')}
                  value={productCategoryCode}
                  onChange={(e) => setProductCategoryCode(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid xs={12} sm={6} md={4}>
                <CustomTextField
                  slotProps={{ input: { min: 0, step: 0.001 } }}
                  type="number"
                  label={t('shipmentForm.meterPriceOverride')}
                  value={meterPriceOverride}
                  onChange={(e) => setMeterPriceOverride(e.target.value)}
                  endDecorator={<OmrSymbol height={16} />}
                  fullWidth
                />
              </Grid>
              <Grid xs={12} sm={6} md={4}>
                <CustomTextField
                  slotProps={{ input: { min: 0, step: 0.001 } }}
                  type="number"
                  label={t('shipmentForm.extraCost')}
                  value={extraCost}
                  onChange={(e) => setExtraCost(e.target.value)}
                  endDecorator={<OmrSymbol height={16} />}
                  fullWidth
                />
              </Grid>

              {formType === "edit" && (
                <Grid xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ mr: 1 }}>{t('shipmentForm.invoicedLabel')}</Typography>
                    {invoiced ? (
                      <Tooltip title={t('shipments.tooltipInvoiced')}>
                        <ListAltIcon color='success' />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t('shipments.tooltipNotInvoiced')}>
                        <ListAltIcon sx={{ color: red[500] }} />
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              )}

              {isAddFlow || (formType === 'edit' && shipmentID) ? (
                <Grid xs={12}>
                  <ShipmentAppliedRulesPanel
                    draftMode={isAddFlow}
                    shipmentId={isAddFlow ? null : id}
                    stagedRows={isAddFlow ? stagedManualLines : undefined}
                    onStagedChange={isAddFlow ? setStagedManualLines : undefined}
                    appliedRules={
                      !isAddFlow && shipment && typeof shipment === 'object'
                        ? shipment.applied_rules
                        : []
                    }
                    onRefresh={
                      !isAddFlow && shipmentID ? () => getShipment(shipmentID) : undefined
                    }
                  />
                </Grid>
              ) : null}

              {/* Status, location & event type */}
              <Grid xs={12} sx={{ mt: 1 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography level="title-lg" sx={{ mb: 0.5 }}>{t('shipmentForm.sectionStatusLocation')}</Typography>
                <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  {t('shipmentForm.statusLocationHint')}
                </Typography>
              </Grid>

              <Grid xs={12} sm={6} md={4}>
                <Box
                  ref={locationFieldRef}
                  tabIndex={-1}
                  sx={{ scrollMarginTop: 24, outline: "none" }}
                >
                  <LocationOptionList
                    label={t('widgets.location')}
                    required={true}
                    defaultValue={currentLocation}
                    errorMessage={locationError}
                    onChange={(event, value) => {
                      setCurrentLocation(value ?? null);
                      setLocationError("");
                    }}
                  />
                </Box>
              </Grid>

              <Grid xs={12} sm={6} md={4}>
                <StatusOptionList
                  required={true}
                  status={status}
                  setStatus={(newValue) => setStatus(newValue)}
                  showAll={false}
                  disabled={false}
                />
              </Grid>

              {canListEventTypes && (
                <Grid xs={12} sm={6} md={4}>
                  <TrackingEventTypeOptionList
                    label={t('widgets.eventType')}
                    defaultValue={eventTypeOpt}
                    onChange={(event, value) => {
                      setEventTypeOpt(value ?? null);
                      setEventType((value && value.code) || '');
                    }}
                  />
                </Grid>
              )}

              {/* Notes & tags */}
              <Grid xs={12} sx={{ mt: 1 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography level="title-lg" sx={{ mb: 0.5 }}>{t('shipmentForm.sectionNotesServices')}</Typography>
                <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  {t('shipmentForm.notesServicesHint')}
                </Typography>
              </Grid>

              <Grid xs={12}>
                {formType === 'edit' && shipment && shipment.deleted ? (
                  <Alert variant="soft" color="warning" sx={{ borderRadius: 'md', mb: 1.5 }}>
                    <Typography level="body-sm">{t('shipmentForm.shipmentDeletedHint')}</Typography>
                  </Alert>
                ) : null}
                <CustomTextField
                  type="text"
                  onChange={(event) => setNote(event.target.value)}
                  value={note}
                  label={t('shipmentForm.note')}
                  fullWidth
                />
              </Grid>

              <Grid xs={12}>
                <ShipmentTagsOptionList
                  value={tagNames}
                  onChange={setTagNames}
                  hintTags={shipment && Array.isArray(shipment.tags) ? shipment.tags : undefined}
                />
              </Grid>
            </Grid>

            {/* Actions Section */}
            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid xs={12} md={8}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  useFlexGap
                  sx={{
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    flexWrap: { sm: 'wrap' },
                  }}
                >
                  {formType === "edit" ? (
                    <>
                      <PermissionGate permission={PERMISSIONS.SHIPMENTS_UPDATE}>
                        <Button
                          loading={isSubmitLoading}
                          disabled={weightLockSaving}
                          type="submit"
                          startDecorator={<SaveIcon sx={{ fontSize: '1.125rem' }} />}
                          sx={{ justifyContent: 'center' }}
                        >
                          {t('shipmentForm.update')}
                        </Button>
                      </PermissionGate>
                      <PermissionGate permission={PERMISSIONS.WHATSAPP_SEND_SHIPMENT_NOTIFICATION}>
                        <Button
                          loading={loadingMsg}
                          onClick={() => sendWhatsAppMessage(id)}
                          startDecorator={<WhatsAppIcon sx={{ fontSize: '1.125rem' }} />}
                          sx={{ justifyContent: 'center' }}
                        >
                          {t('shipmentForm.sendWhatsApp')}
                        </Button>
                      </PermissionGate>
                      <PermissionGate permission={PERMISSIONS.TRACKING_EVENTS_LIST}>
                        <Button
                          variant="soft"
                          color="primary"
                          onClick={() => navigate(`/shipment/${id}/tracking`)}
                          startDecorator={<TimelineIcon sx={{ fontSize: '1.125rem' }} />}
                          sx={{ justifyContent: 'center' }}
                        >
                          {t('customer.trackShipment')}
                        </Button>
                      </PermissionGate>
                      <PermissionGate permission={PERMISSIONS.SHIPMENTS_CREATE}>
                        <Button
                          onClick={() => navigate("../../Shipment/AddShipment")}
                          startDecorator={<AddIcon sx={{ fontSize: '1.125rem' }} />}
                          sx={{ justifyContent: 'center' }}
                        >
                          {t('shipmentForm.addNewShipment')}
                        </Button>
                      </PermissionGate>
                    </>
                  ) : (
                    <PermissionGate permission={PERMISSIONS.SHIPMENTS_CREATE}>
                      <Button loading={isSubmitLoading} type="submit">{t('actions.submit')}</Button>
                    </PermissionGate>
                  )}
                </Stack>
              </Grid>

              <Grid xs={12} md={4}>
                {formType === "edit" && (
                  <Box sx={{ mt: { xs: 2, md: 0 } }}>
                    <ShipmentSticker shipments={[shipment]} />
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Data Grids Section */}
            {shipmentID && (
              <>
                <Box sx={{ mt: 4 }}>
                  <Typography level="h4" sx={{ mb: 2 }}>{t('shipmentForm.notifications')}</Typography>
                  <DataGrid
                    hideFooter={true}
                    rows={notifications || []}
                    columns={[
                      {
                        field: 'shipment_status',
                        headerName: t('shipmentForm.notifColShipmentStatus'),
                        flex: 2,
                        minWidth: 160,
                        renderCell: (params) => renderNotifLocalized(
                          params.row.shipment_status_name,
                          params.row.shipment_status_name_ar,
                          params.row.shipment_status,
                        ),
                      },
                      { field: 'channel', headerName: t('shipmentForm.notifColChannel'), flex: 1, minWidth: 90, renderCell: () => (<WhatsAppIcon color="success" />) },
                      { field: 'notification_status', headerName: t('shipmentForm.notifColNotificationStatus'), flex: 1.5, minWidth: 140, renderCell: (params) => notificationStatusIcon(params.row.notification_status) },
                      // Localized location / event_type snapshots explain why two
                      // messages for the same status are not duplicates when the
                      // hub or event type differs. See
                      // FRONTEND_AUTOMATIC_WHATSAPP_NOTIFICATIONS_GUIDE §5.
                      {
                        field: 'location',
                        headerName: t('shipmentForm.notifColLocation'),
                        flex: 1.5,
                        minWidth: 140,
                        renderCell: (params) => renderNotifLocalized(
                          params.row.location_name,
                          params.row.location_name_ar,
                          params.row.location,
                        ),
                      },
                      {
                        field: 'event_type',
                        headerName: t('shipmentForm.notifColEventType'),
                        flex: 2,
                        minWidth: 160,
                        renderCell: (params) => renderNotifLocalized(
                          params.row.event_type_name,
                          params.row.event_type_name_ar,
                          params.row.event_type,
                        ),
                      },
                      { field: 'created_at', headerName: t('shipmentForm.notifColCreatedAt'), flex: 2, minWidth: 160, renderCell: (params) => formatTime(params.row.created_at) },
                    ]}
                    loading={isLoading}
                  />
                </Box>

                <Box sx={{ mt: 4 }}>
                  <Typography level="h4" sx={{ mb: 2 }}>{t('shipmentForm.payments')}</Typography>
                  <DataGrid
                    hideFooter={true}
                    rows={shipment.payments || []}
                    columns={getShipmentFormPaymentColumns(t)}
                    loading={isLoading}
                    getRowHeight={() => 'auto'}
                    sx={{
                      minHeight: 200,
                      '& .MuiDataGrid-cell': {
                        alignItems: 'flex-start',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: 1.5,
                        py: 1,
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </FormControl>
        </form>
      </Box>

      {/* Notifications/Snackbars */}
      <Snackbar
        autoHideDuration={3000}
        open={successUpdate}
        variant="soft"
        color="success"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setSuccessUpdate(false);
        }}
      >
        {t('shipmentForm.snackbarUpdated')}
      </Snackbar>

      <Snackbar
        autoHideDuration={3000}
        open={failedUpdate}
        variant="soft"
        color="danger"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setFailedUpdate(false);
        }}
      >
        {t('shipmentForm.snackbarUpdateFailed', {
          detail:
            typeof errorMessage === 'string'
              ? errorMessage
              : errorMessage?.message || String(errorMessage ?? ''),
        })}
      </Snackbar>

      <Snackbar
        autoHideDuration={3000}
        open={successSentMsg}
        variant="soft"
        color="success"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setSuccessSentMsg(false);
        }}
      >
        {t('shipmentForm.snackbarWhatsAppOk')}
      </Snackbar>

      <Snackbar
        autoHideDuration={3000}
        open={failedSentMsg}
        variant="soft"
        color="danger"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setFailedSentMsg(false);
        }}
      >
        {t('shipmentForm.snackbarWhatsAppFail')}
      </Snackbar>

      {trackingPromptModal}
    </Box>
  )
}